(() => {
  'use strict';
  const $ = (s, r = document) => (r || document).querySelector(s);
  const $$ = (s, r = document) => Array.from((r || document).querySelectorAll(s));
  const body = document.body;
  const routes = window.SATISFACTION?.routes || {};
  const root = window.Shopify?.routes?.root || routes.root || '/';
  const checkoutUrl = routes.checkout || `${root.replace(/\/$/, '')}/checkout`;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.SATISFACTION?.motion === false;

  // Cinematic entry + ambient loop. The intro is deterministic and replayable:
  // it always starts after a successful age-gate entry, with a clean animation state.
  const intro = $('[data-site-intro]');
  let introTimer = 0;
  let introRunId = 0;
  const CINEMATIC_MS = 1800;

  const finishCinematicIntro = (runId) => {
    if (!intro || runId !== introRunId) return;
    intro.classList.add('is-complete');
    intro.classList.remove('is-running');
    intro.setAttribute('aria-hidden', 'true');
    body.classList.remove('is-entering');
  };

  const runCinematicIntro = () => {
    if (!intro) return;
    const motionEnabled = window.SATISFACTION?.motion !== false && window.SATISFACTION?.intro !== false;

    if (!motionEnabled) {
      window.clearTimeout(introTimer);
      intro.classList.remove('is-running');
      intro.classList.add('is-complete');
      intro.setAttribute('aria-hidden', 'true');
      body.classList.remove('is-entering');
      return;
    }

    const runId = ++introRunId;
    window.clearTimeout(introTimer);

    // Reset all animation classes before every run. Two paints prevent browsers from
    // coalescing the reset + start and skipping the cinematic sequence.
    intro.classList.remove('is-complete', 'is-running');
    intro.setAttribute('aria-hidden', 'false');
    body.classList.add('is-entering');
    void intro.offsetWidth;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!intro || runId !== introRunId) return;
        void intro.offsetWidth;
        intro.classList.add('is-running');
        introTimer = window.setTimeout(() => finishCinematicIntro(runId), CINEMATIC_MS);
      });
    });
  };

  $$('video[autoplay]').forEach((video) => {
    video.muted = true;
    video.playsInline = true;
    const tryPlay = () => video.play().catch(() => {});
    tryPlay();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) video.pause();
      else tryPlay();
    }, { passive: true });
  });

  // Lightweight navigation transition. It is enabled only after the visitor has
  // completed the initial age gate, and never changes the destination or page flow.
  const navLoader = $('[data-site-nav-loader]');
  const NAV_TRANSITION_MS = 850;
  let navTransitioning = false;
  const showNavTransition = (href) => {
    if (!navLoader || navTransitioning) return false;
    navTransitioning = true;
    navLoader.classList.remove('is-running', 'is-complete');
    navLoader.setAttribute('aria-hidden', 'false');
    void navLoader.offsetWidth;
    navLoader.classList.add('is-running');
    window.setTimeout(() => {
      navLoader.classList.add('is-complete');
      window.location.href = href;
    }, NAV_TRANSITION_MS);
    return true;
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor || !navLoader || navTransitioning) return;
    if (anchor.closest('[data-age-gate]') || anchor.closest('[data-site-nav-loader]')) return;
    if (anchor.hasAttribute('download') || anchor.target === '_blank' || anchor.target === '_parent' || anchor.target === '_top') return;

    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:') || rawHref.startsWith('javascript:')) return;

    let url;
    try { url = new URL(anchor.href, window.location.href); } catch (_) { return; }
    if (!/^https?:$/.test(url.protocol)) return;
    if (url.href === window.location.href) return;

    let verified = false;
    try { verified = window.sessionStorage.getItem('satisfaction_age_verified_v1') === '1'; } catch (_) {}
    if (!verified) return;

    event.preventDefault();
    showNavTransition(url.href);
  }, true);

  const route = (r, fallback) => r || `${root}${fallback.replace(/^\//, '')}`;
  const parseJson = async (res) => {
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.description || data?.message || 'Request failed');
    return data;
  };
  const money = (cents, currency) => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || window.SATISFACTION?.currency || 'INR', maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100); }
    catch { return String((Number(cents) || 0) / 100); }
  };
  const setCount = (n) => $$('[data-cart-count]').forEach((el) => { el.textContent = String(n || 0); });
  const toast = (msg) => { const el = $('[data-toast]'); if (!el) return; el.textContent = msg; el.classList.add('is-visible'); clearTimeout(window.__satToast); window.__satToast = setTimeout(() => el.classList.remove('is-visible'), 2500); };

  // Age gate — shown only on the initial landing page and persisted for the visit/session.
  // Normal navigation/product taps never ask again during the same visit.
  const gate = $('[data-age-gate]');
  const AGE_KEY = 'satisfaction_age_verified_v1';
  let ageVerified = false;
  try { ageVerified = window.sessionStorage.getItem(AGE_KEY) === '1'; } catch (_) {}

  if (gate) {
    const enter = $('[data-age-yes]', gate);

    if (ageVerified) {
      gate.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('age-required', 'age-blocked');
      body.classList.remove('age-locked');
      // A verified visitor goes straight to the store; never leave the intro layer visible.
      intro?.classList.add('is-complete');
      intro?.setAttribute('aria-hidden', 'true');
    } else {
      gate.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('age-required');
      body.classList.add('age-locked');

      enter?.addEventListener('click', () => {
        try { window.sessionStorage.setItem(AGE_KEY, '1'); } catch (_) {}
        gate.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('age-required', 'age-blocked');
        body.classList.remove('age-locked');

        // Start the cinematic only after the age gate has closed.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => runCinematicIntro());
        });
      });
    }
    $('[data-age-no]', gate)?.addEventListener('click', (event) => {
      event.preventDefault();
      document.documentElement.classList.add('age-blocked');
      gate.setAttribute('aria-hidden', 'false');
      gate.querySelector('.age-gate__card')?.setAttribute('data-blocked', 'true');
      const heading = gate.querySelector('h2');
      const copy = gate.querySelector('.age-gate__card>p:not(.eyebrow)');
      if (heading) heading.textContent = 'Access restricted';
      if (copy) copy.textContent = 'You must be 18 or older to view this store.';
      if (enter) enter.hidden = true;
      event.currentTarget.hidden = true;
      setTimeout(() => { window.location.replace('https://www.google.com/'); }, 900);
    });
  }

  // The cinematic entry runs only from the initial landing-page age confirmation.
  // Secondary pages do not invoke the age gate or replay the entry sequence.
  // The intro element exists in the shared layout on every page, so make sure it
  // is explicitly hidden on secondary pages; otherwise it can sit above the page
  // content and make product/collection pages appear blank.
  if (!gate && intro) {
    window.clearTimeout(introTimer);
    intro.classList.remove('is-running');
    intro.classList.add('is-complete');
    intro.setAttribute('aria-hidden', 'true');
    body.classList.remove('is-entering');
  }

  // Defensive viewport/scroll reset. Safari/iOS and bfcache can restore a stale
  // interaction lock from a previous page. Never let that lock the product/cart pages.
  const clearStaleLocks = () => {
    const ageVisible = gate?.getAttribute('aria-hidden') === 'false';
    if (!ageVisible) {
      body.classList.remove('age-locked', 'is-entering', 'menu-open', 'cart-open');
      document.documentElement.classList.remove('age-required', 'age-blocked');
      menu?.setAttribute('aria-hidden', 'true');
      drawer?.setAttribute('aria-hidden', 'true');
    }
  };
  window.addEventListener('pageshow', clearStaleLocks);

  // Announcement dismissal.
  const announcement = $('[data-announcement]');
  if (announcement && sessionStorage.getItem('satisfaction_announcement_closed_v2') === '1') announcement.remove();
  $('[data-announcement-close]')?.addEventListener('click', () => { sessionStorage.setItem('satisfaction_announcement_closed_v2', '1'); announcement?.remove(); });

  // Sticky header and scroll progress.
  const header = $('[data-header]');
  const progress = $('[data-scroll-progress]');
  const onScroll = () => {
    header?.classList.toggle('is-scrolled', window.scrollY > 12);
    if (progress) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`;
    }
  };
  onScroll(); window.addEventListener('scroll', onScroll, { passive: true });

  // Mobile drawer.
  const menu = $('[data-mobile-menu]');
  const closeMenu = () => { menu?.setAttribute('aria-hidden', 'true'); body.classList.remove('menu-open'); };
  $('[data-menu-open]')?.addEventListener('click', () => { menu?.setAttribute('aria-hidden', 'false'); body.classList.add('menu-open'); });
  $('[data-menu-close]')?.addEventListener('click', closeMenu);
  $$('.mobile-drawer a').forEach(a => a.addEventListener('click', closeMenu));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu?.getAttribute('aria-hidden') === 'false') closeMenu();
  });
  $('[data-cart-open]', menu)?.addEventListener('click', closeMenu);

  // Reveal motion.
  if (!reduced && 'IntersectionObserver' in window) {
    $$('[data-reveal]').forEach(el => el.classList.add('js-reveal'));
    const io = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); } }), { threshold: 0.08, rootMargin: '0px 0px -50px' });
    $$('[data-reveal]').forEach(el => io.observe(el));
  } else $$('[data-reveal]').forEach(el => el.classList.add('is-visible'));

  // Subtle 3D tilt. Disabled for coarse pointers / reduced motion.
  if (!reduced && window.matchMedia('(pointer:fine)').matches) {
    $$('[data-tilt]').forEach(card => {
      const reset = () => { card.style.setProperty('--rx', '0deg'); card.style.setProperty('--ry', '0deg'); };
      card.addEventListener('pointermove', e => { const r = card.getBoundingClientRect(); const x = ((e.clientX - r.left) / r.width) - .5; const y = ((e.clientY - r.top) / r.height) - .5; card.style.setProperty('--rx', `${(-y * 5).toFixed(2)}deg`); card.style.setProperty('--ry', `${(x * 7).toFixed(2)}deg`); });
      card.addEventListener('pointerleave', reset); reset();
    });
  }

  // Product gallery.
  const slides = $$('[data-gallery-slide]');
  const thumbs = $$('[data-gallery-thumb]');
  const showSlide = i => { slides.forEach((s, n) => s.classList.toggle('is-active', n === i)); thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i)); };
  thumbs.forEach(t => t.addEventListener('click', () => showSlide(Number(t.dataset.galleryThumb))));

  // Product options + native Shopify cart AJAX.
  const form = $('[data-product-form]');
  if (form) {
    const productData = JSON.parse($('[data-product-json]')?.textContent || '{"variants":[]}');
    const variants = productData.variants || [];
    const select = $('[data-variant-select]', form);
    const priceEl = $('[data-product-price]', form.closest('[data-product-section]') || document);
    const compareEl = $('.product-compare', form.closest('[data-product-section]') || document);
    const submit = $('[data-add-to-cart]', form);
    const status = $('[data-form-status]', form);
    const chosen = () => { const out = {}; $$('input[data-option-position]:checked', form).forEach(i => out[i.dataset.optionPosition] = i.value); return out; };
    const findVariant = () => {
      const selected = chosen();
      if (!Object.keys(selected).length) return variants.find(v => String(v.id) === String(select?.value)) || variants.find(v => v.available) || variants[0];
      return variants.find(v => Object.keys(selected).every(p => v[`option${p}`] === selected[p])) || null;
    };
    const sync = () => {
      const v = findVariant();
      if (!v) { if (submit) { submit.disabled = true; submit.innerHTML = 'Select an option <span>+</span>'; } return; }
      if (select) select.value = String(v.id);
      if (priceEl) priceEl.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: window.SATISFACTION?.currency || 'INR' }).format(Number(v.price || 0) / 100);
      if (compareEl) { const c = Number(v.compare_at_price || 0); compareEl.textContent = c > Number(v.price || 0) ? new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style:'currency', currency:window.SATISFACTION?.currency || 'INR' }).format(c/100) : ''; compareEl.hidden = !(c > Number(v.price || 0)); }
      if (submit) { submit.disabled = !v.available; submit.innerHTML = v.available ? 'Add to cart <span>+</span>' : 'Sold out'; }
      const sticky = document.querySelector('.mobile-product-checkout');
      if (sticky) { sticky.disabled = !v.available; sticky.textContent = v.available ? 'Buy now · Checkout' : 'Sold out'; }
    };
    $$('input[data-option-position]', form).forEach(i => i.addEventListener('change', sync)); sync();
    const buyNow = $('[data-buy-now]', form);
    buyNow?.addEventListener('click', async () => {
      const v = findVariant();
      if (!v?.available || !buyNow) return;
      const qty = Math.max(1, Number($('[name="quantity"]', form)?.value || 1));
      buyNow.disabled = true;
      buyNow.innerHTML = 'Preparing checkout…';
      try {
        await parseJson(await fetch(route(routes.cartAdd, 'cart/add') + '.js', {
          method: 'POST',
          headers: {'Content-Type':'application/json','Accept':'application/json'},
          body: JSON.stringify({items:[{id:Number(v.id), quantity:qty}]})
        }));
        window.location.assign(checkoutUrl);
      } catch (err) {
        if (status) status.textContent = err.message || 'Could not start checkout.';
        toast('Could not start checkout');
        buyNow.disabled = false;
        buyNow.innerHTML = 'Buy now <span>↗</span>';
      }
    });
    form.addEventListener('submit', async e => {
      e.preventDefault(); const v = findVariant(); if (!v?.available) return; const qty = Math.max(1, Number($('[name="quantity"]', form)?.value || 1)); submit.disabled = true; submit.innerHTML = 'Adding…';
      try { await parseJson(await fetch(route(routes.cartAdd, 'cart/add') + '.js', { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ items:[{ id:Number(v.id), quantity:qty }] }) })); toast('Added to your cart'); await refreshCart(); openCart(); if (status) status.textContent = 'Added to your cart.'; }
      catch(err) { if (status) status.textContent = err.message || 'Could not add this item.'; toast('Could not add that item'); }
      finally { sync(); }
    });
  }

  // Mobile product checkout action. Keep the real Shopify checkout reachable even
  // when the product form is below the fold; this is intentionally a real checkout
  // action, not a fake UI control.
  const productForm = $('[data-product-form]');
  if (productForm) {
    const stickyBuy = document.createElement('button');
    stickyBuy.type = 'button';
    stickyBuy.className = 'mobile-product-checkout button button--primary';
    stickyBuy.textContent = 'Buy now · Checkout';
    stickyBuy.setAttribute('aria-label', 'Buy now and continue to secure checkout');
    const currentBuy = $('[data-buy-now]', productForm);
    stickyBuy.disabled = !!currentBuy?.disabled;
    if (stickyBuy.disabled) stickyBuy.textContent = 'Sold out';
    document.body.appendChild(stickyBuy);
    stickyBuy.addEventListener('click', () => {
      const buy = $('[data-buy-now]', productForm);
      if (buy && !buy.disabled) { buy.click(); return; }
      stickyBuy.disabled = true;
      stickyBuy.textContent = 'Unavailable';
    });
  }

  // Cart drawer.
  const drawer = $('[data-cart-drawer]'); let busy = false;
  const escapeHtml = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
  const renderCart = cart => {
    if (!drawer) return; setCount(cart.item_count); const items = $('[data-cart-items]', drawer); const total = $('[data-cart-total]', drawer); const checkout = $('button[name="checkout"]', drawer); if (!items) return; if (total) total.textContent = money(cart.total_price, cart.currency);
    if (checkout) checkout.disabled = cart.item_count === 0;
    if (!cart.items.length) { items.innerHTML = `<div class="drawer-empty"><h3>Your cart is empty.</h3><p>There is plenty to explore.</p><a class="button button--soft" href="${escapeHtml((routes.root || root) + 'collections/all')}">Explore the store <span>↗</span></a></div>`; return; }
    items.innerHTML = cart.items.map(item => `<div class="drawer-line" data-line-key="${escapeHtml(item.key)}"><a class="drawer-line__image" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(item.image)}&width=320" alt="${escapeHtml(item.product_title)}" loading="lazy">` : ''}</a><div class="drawer-line__body"><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}<div class="drawer-line__bottom"><div class="quantity quantity--small"><button type="button" data-drawer-minus="${escapeHtml(item.key)}" aria-label="Decrease">−</button><input type="number" min="0" value="${Number(item.quantity) || 0}" data-drawer-qty="${escapeHtml(item.key)}"><button type="button" data-drawer-plus="${escapeHtml(item.key)}" aria-label="Increase">+</button></div><span>${money(item.final_line_price, cart.currency)}</span></div><button class="drawer-line__remove" type="button" data-drawer-remove="${escapeHtml(item.key)}">Remove</button></div></div>`).join('');
  };
  async function refreshCart() { if (!drawer) return null; try { const data = await parseJson(await fetch(route(routes.cartJs, 'cart') + '.js', { headers:{Accept:'application/json'} })); renderCart(data); return data; } catch { return null; } }
  async function changeLine(key, qty) { if (busy) return; busy = true; try { const cart = await parseJson(await fetch(route(routes.cartChange, 'cart/change') + '.js', { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ id:key, quantity:Math.max(0, Number(qty)||0) }) })); renderCart(cart); toast(qty === 0 ? 'Removed from your cart' : 'Cart updated'); } catch { toast('Could not update your cart'); } finally { busy = false; } }
  function openCart() { drawer?.setAttribute('aria-hidden','false'); body.classList.add('cart-open'); refreshCart(); }
  function closeCart() { drawer?.setAttribute('aria-hidden','true'); body.classList.remove('cart-open'); }
  window.__satisfactionOpenCart = openCart;
  $$('[data-cart-open]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); closeMenu(); openCart(); }));
  $$('[data-cart-close]').forEach(b => b.addEventListener('click', closeCart));
  drawer?.addEventListener('click', e => { const r=e.target.closest('[data-drawer-remove]'); if(r){changeLine(r.dataset.drawerRemove,0);return;} const p=e.target.closest('[data-drawer-plus]'); const m=e.target.closest('[data-drawer-minus]'); if(p||m){const k=(p||m).dataset.drawerPlus || (m||{}).dataset.drawerMinus; const row=(p||m).closest('.drawer-line'); const input=$('[data-drawer-qty]',row); changeLine(k,Math.max(0,Number(input?.value||0)+(p?1:-1)));} });


  // Quick add.
  document.addEventListener('submit', async e => { const f=e.target.closest('[data-quick-add]'); if(!f)return; e.preventDefault(); const b=$('button[type="submit"]',f), id=Number($('[name="id"]',f)?.value||0); if(!id)return; b.disabled=true; b.innerHTML='Adding…'; try { await parseJson(await fetch(route(routes.cartAdd,'cart/add')+'.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id,quantity:1}]})})); toast('Added to your cart'); await refreshCart(); openCart(); } catch { toast('Could not add that item'); } finally { b.disabled=false; b.innerHTML='Quick add <span>+</span>'; } });

  // Quantity controls on product and cart pages.
  document.addEventListener('click', e => { const b=e.target.closest('[data-qty-minus],[data-qty-plus],[data-cart-qty-minus],[data-cart-qty-plus]'); if(!b)return; const id=b.dataset.target; const input=id?document.getElementById(id):b.parentElement?.querySelector('input[type="number"]'); if(!input)return; const min=Number(input.min||0), max=Number(input.max||999), next=Math.max(min,Math.min(max,Number(input.value||min)+(b.matches('[data-qty-plus],[data-cart-qty-plus]')?1:-1))); input.value=next; if(input.closest('.cart-page')) input.closest('form')?.requestSubmit(); });

  // Filters.
  const filterDrawer = $('[data-filters]'); $('[data-filter-open]')?.addEventListener('click',()=>filterDrawer?.setAttribute('aria-hidden','false')); $('[data-filter-close]')?.addEventListener('click',()=>filterDrawer?.setAttribute('aria-hidden','true'));

  document.addEventListener('keydown', e => { if(e.key==='Escape'){closeCart();closeMenu();filterDrawer?.setAttribute('aria-hidden','true');} });
  refreshCart();
})();
